import TagManager from 'react-gtm-module'
import { EventType } from '../segment/types'
import { JoinEventRes, Addon, Summary } from 'src/services/types/joinEventV2'
import { Challenge } from 'src/services/types/searchEvents'
import { Receipt } from './../../../services/types/joinEventV2'

type GTMProduct = {
    id: number
    name: string
    category: Category
    price: number
    quantity: number
    brand: string
    sku: number
}

type GTMRemarketingProduct = {
    dynx_itemid: number
    dynx_itemid2: string
    dynx_pagetype: Category
    dynx_totalvalue: number
}

enum Category {
    VirtualRace = 'Virtual Race',
    Exercise = 'Exercise',
    FitnessChallenge = 'Fitness Challenge',
    Store = 'Store',
    Trainers = 'Trainers',
    Blog = 'Blog',
    Cart = 'Cart',
    Misc = 'Misc'
}

enum ProductCategoryByEventType {
    'Virtual Race' = EventType.Races,
    'Fitness Challenge' = EventType.Challenges,
    'Store' = EventType.Store
}

export function tagsSale(receipt: Receipt) {
    const purchasedChallenges = receipt.bundle.challenges
    const taggedProducts = combinedChallengesAndAddOns(purchasedChallenges)
    const productsForRemarketing: GTMRemarketingProduct[] = taggedProducts.map(
        (product) => {
            return {
                dynx_itemid: product.id,
                dynx_itemid2: product.name,
                dynx_pagetype: product.category,
                dynx_totalvalue: product.price
            }
        }
    )

    TagManager.dataLayer({
        dataLayer: {
            event: 'purchase',
            price: receipt.total,
            currency: 'USD',
            itemIds: [`${receipt.challengeId}`],
            ecommerce: {
                purchase: {
                    actionField: {
                        id: receipt.transactionId,
                        affiliation: 'YES.FIT',
                        revenue: receipt.charged
                    },
                    products: taggedProducts
                }
            },
            remarketing: { items: productsForRemarketing }
        }
    })
}

export function tagsViewItem(eventType: EventType, data: JoinEventRes, userId?: number) {
    const { summary, addons } = data
    const productCategory: Category = ProductCategoryByEventType[eventType]
    const price = getPriceByCategory(productCategory, addons, summary)

    TagManager.dataLayer({
        dataLayer: {
            event: 'view_item',
            price: price,
            currency: 'USD',
            userId: userId,
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            itemIds: [`${summary.challengeId}`],
            ecommerce: {
                detail: {
                    products: [
                        {
                            id: summary.challengeId,
                            name: summary.name,
                            brand: 'YES.FIT',
                            category: productCategory,
                            quantity: 1,
                            price: price
                        }
                    ]
                }
            },
            remarketing: {
                dynx_itemid: summary.challengeId,
                dynx_itemid2: summary.name,
                dynx_pagetype: productCategory,
                dynx_totalvalue: price
            }
        }
    })
}

export function tagsRegistration(response) {
    TagManager.dataLayer({
        dataLayer: {
            event: 'sign_up',
            method: 'email',
            userId: response.userId
        }
    })
}

export function tagsAddToCart(item: any) {
    const addedProduct: GTMProduct = mapChallenge(item)

    TagManager.dataLayer({
        dataLayer: {
            event: 'add_to_cart',
            price: addedProduct.price,
            currency: 'USD',
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            itemIds: [addedProduct.id],
            ecommerce: {
                currencyCode: 'USD',
                add: {
                    products: addedProduct
                }
            },
            remarketing: {
                dynx_itemid: addedProduct?.id,
                dynx_itemid2: addedProduct?.name,
                dynx_pagetype: addedProduct?.category,
                dynx_totalvalue: addedProduct?.price
            }
        }
    })
}

export function tagsAbandonCart(challenges) {
    const taggedProducts = combinedChallengesAndAddOns(challenges)
    TagManager.dataLayer({
        dataLayer: {
            event: 'remove_from_cart',
            ecommerce: {
                remove: {
                    products: taggedProducts
                }
            }
        }
    })
}

export function tagsInitiateJoinRace(challenge) {
    TagManager.dataLayer({
        dataLayer: {
            event: 'begin_checkout',
            value: challenge.price,
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            itemIds: [`${challenge.challengeId}`],
            currency: 'USD',
            quantity: '1',
            category: 'Virtual Race',
            ecommerce: {
                checkout_option: {
                    actionField: {
                        step: 'begin_checkout'
                    },
                    products: [
                        {
                            name: challenge.name,
                            id: challenge.challengeId,
                            price: challenge.price,
                            brand: 'YES.FIT',
                            category: 'Virtual Race',
                            quantity: '1'
                        }
                    ]
                }
            }
        }
    })
}

export const logPageView = (url: string) => {
    const tagManagerArgs = {
        dataLayer: {
            event: 'pageView',
            page: url
        }
    }
    TagManager.dataLayer(tagManagerArgs)
}

function getPriceByCategory(productCategory: any, addons: Addon[], summary: Summary) {
    return productCategory === EventType.Store
        ? addons.reduce((acc, addon) => acc + addon.itemPrice, 0)
        : summary.minimumPrice
}

function combinedChallengesAndAddOns(challenges) {
    return challenges.flatMap((c) => {
        const challenge: GTMProduct = mapChallenge(c)
        const challengeAddOns: GTMProduct[] = (c.items.length >= 1 && mapAddOns(c)) ?? []
        return [challenge, ...challengeAddOns]
    }) as GTMProduct[]
}

function mapChallenge(challenge: Challenge): GTMProduct {
    const productCategory = challenge.isSwag
        ? Category.Store
        : challenge.is30Day
        ? Category.FitnessChallenge
        : Category.VirtualRace

    return {
        id: challenge.challengeId,
        name: challenge.name,
        category: productCategory,
        price: challenge.price,
        quantity: 1,
        brand: 'YES.FIT',
        sku: challenge.challengeId
    }
}

function mapAddOns(purchasedChallenge: any) {
    const challengeAddOns: GTMProduct[] = []
    challengeAddOns.push(
        ...purchasedChallenge.items.map((addon) => ({
            id: addon.challengeAddonId,
            name: addon.description,
            category: addon.challengeAddonType.name,
            price: addon.itemPrice,
            quantity: 1,
            brand: 'YES.FIT',
            sku: addon.challengeAddonId
        }))
    )
    return challengeAddOns
}

// Decided that at the current moment we aren't using dynamic remarketing data on the page_view events.
// Should we ever choose to do so, this enum and function can be used to map the url path to a specific category for page_view event.

// enum UrlPathMap {
//     'races' = PageType.VirtualRace,
//     'exercises' = PageType.Exercise,
//     'challenges' = PageType.FitnessChallenge,
//     'store' = PageType.Store,
//     'trainers' = PageType.Trainers,
//     'blog' = PageType.Blog,
//     'cart' = PageType.Cart
// }
// const getPageType = (url: string) => {
//     const rootPath = url.split('/')[1]
//     return (UrlPathMap[rootPath] as string) ?? PageType.Misc
// }
